import { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { setBadgesPreviewMode, notifyBadgesChanged } from '@/lib/moduleBadges';

// Set global axios timeout (20 seconds - mobile connections can be slow)
axios.defaults.timeout = 20000;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [realUser, setRealUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // viewMode: null | { id, name, company, email, daily_rate, ... } (specific employee object)
  const [viewMode, setViewMode] = useState(null);
  const interceptorRef = useRef(null);

  const fetchUser = async () => {
    // Retry once on network error
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await axios.get(`${API}/users/me`, { timeout: 20000 });
        setRealUser(response.data);
        setLoading(false);
        return;
      } catch (error) {
        console.error('Failed to fetch user:', error);
        if (error.response && error.response.status === 401) {
          logout();
          setLoading(false);
          return;
        }
        // Retry on network/timeout errors
        if (!error.response && attempt === 0) {
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Axios interceptor: append view_as param when in preview mode
  useEffect(() => {
    // Remove previous interceptor
    if (interceptorRef.current !== null) {
      axios.interceptors.request.eject(interceptorRef.current);
      interceptorRef.current = null;
    }

    const previewActive = !!(viewMode && viewMode.id && realUser?.role === 'manager');
    if (previewActive) {
      interceptorRef.current = axios.interceptors.request.use((config) => {
        // Only add view_as to GET requests to our API
        if (config.method === 'get' && config.url?.includes('/api/')) {
          const separator = config.url.includes('?') ? '&' : '?';
          // Don't add if already has view_as or if it's a users/me call
          if (!config.url.includes('view_as=') && !config.url.endsWith('/users/me')) {
            config.params = { ...config.params, view_as: viewMode.id };
          }
        }
        return config;
      });
    }

    // Pastilles et cloche suivent le mode : lecture-seule en apercu + refetch
    // immediat (l'intercepteur vient d'etre (des)active, les GET repartent juste)
    setBadgesPreviewMode(previewActive);
    notifyBadgesChanged();

    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.request.eject(interceptorRef.current);
        interceptorRef.current = null;
      }
    };
  }, [viewMode, realUser]);

  // Compute the effective user based on viewMode
  const user = (() => {
    if (!realUser) return null;
    if (!viewMode || realUser.role !== 'manager') return realUser;
    
    // In preview mode: use the selected employee's data
    return {
      ...realUser,
      id: viewMode.id,
      name: viewMode.name,
      email: viewMode.email,
      role: viewMode.role || 'employee',
      company: viewMode.company || 'La Piscine Container',
      daily_rate: viewMode.daily_rate,
      birthday: viewMode.birthday,
      phone: viewMode.phone,
      photo_url: viewMode.photo_url,
      created_at: viewMode.created_at,
      pointage_only: viewMode.pointage_only,
      trial_until: viewMode.trial_until,
      _isPreview: true,
      _realRole: 'manager',
      _realId: realUser.id,
    };
  })();

  const isPreviewMode = viewMode !== null && realUser?.role === 'manager';

  const login = async (email, password) => {
    // Retry up to 2 times on network errors
    let lastError;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await axios.post(`${API}/auth/login`, { email, password }, { timeout: 15000 });
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
        setRealUser(userData);
        setViewMode(null);
        return userData;
      } catch (err) {
        lastError = err;
        // Only retry on network errors (no response), not on 401/400
        if (err.response) throw err;
        if (attempt === 0) await new Promise(r => setTimeout(r, 1000));
      }
    }
    throw lastError;
  };

  const register = async (email, password, name, company = 'La Piscine Container', role = 'employee') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, company, role });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setRealUser(userData);
    setViewMode(null);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setRealUser(null);
    setViewMode(null);
  };

  const updateUser = (userData) => {
    setRealUser(prev => ({ ...prev, ...userData }));
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      realUser,
      token, 
      loading, 
      login, 
      register, 
      logout, 
      updateUser,
      viewMode,
      setViewMode,
      isPreviewMode
    }}>
      {children}
    </AuthContext.Provider>
  );
};
